import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { getParameters, setParameters } from '@atlaskit/native-embeds-common/utils';
import { getRemixVersionForRevision } from '@atlaskit/native-embeds-common/remix-slot-store';

import { isNativeEmbedNode } from './utils/getSelectedNativeEmbedExtension';

export const MAUI_EDITABLE_TEXT_PARAM = 'mauiEditableText';
export const MAUI_EDIT_REVISION_PARAM = 'mauiEditRevisionId';

export type RemixEdit = {
	previousValue: string;
	revisionId: string;
	slot: string;
	value: string;
};

export type RemixVersionCreated = {
	remixAppId: string;
	revisionId?: string;
};

type RemixPayload = Record<string, object | string | undefined>;

const isRecord = (value: object | string | null | undefined): value is RemixPayload =>
	value !== null && typeof value === 'object';

const readStringRecord = (
	value: object | string | null | undefined,
): Record<string, string> | undefined => {
	if (!isRecord(value) || !Object.values(value).every((entry) => typeof entry === 'string')) {
		return undefined;
	}
	return value as Record<string, string>;
};

export const readRemixEdit = (value: object | null): RemixEdit | undefined => {
	if (!isRecord(value)) {
		return undefined;
	}
	const { previousValue, revisionId, slot, value: nextValue } = value;
	if (
		typeof previousValue !== 'string' ||
		typeof revisionId !== 'string' ||
		typeof slot !== 'string' ||
		typeof nextValue !== 'string'
	) {
		return undefined;
	}
	return { previousValue, revisionId, slot, value: nextValue };
};

export const readRemixVersionCreated = (value: object | null): RemixVersionCreated | undefined => {
	if (
		!isRecord(value) ||
		typeof value.remixAppId !== 'string' ||
		value.remixAppId === '' ||
		(typeof value.revisionId !== 'string' && value.revisionId !== undefined)
	) {
		return undefined;
	}
	return {
		remixAppId: value.remixAppId,
		revisionId: value.revisionId,
	};
};

export const readEditorHistoryAction = (value: object | null): 'redo' | 'undo' | undefined => {
	if (!isRecord(value)) {
		return undefined;
	}
	return value.action === 'undo' || value.action === 'redo' ? value.action : undefined;
};

export const getMauiEditRevisionId = (node: PMNode): string | undefined => {
	const parameters: object | null = node.attrs.parameters;
	if (!isRecord(parameters)) {
		return undefined;
	}
	const revisionId = parameters[MAUI_EDIT_REVISION_PARAM];
	return typeof revisionId === 'string' ? revisionId : undefined;
};

export const applyRemixEdit = (
	tr: Transaction,
	node: PMNode,
	pos: number,
	edit: RemixEdit,
): Transaction | undefined => {
	const parametersValue: object | null = node.attrs.parameters;
	const parameters = isRecord(parametersValue) ? parametersValue : {};
	const overrides = readStringRecord(parameters[MAUI_EDITABLE_TEXT_PARAM]) ?? {};
	if (
		overrides[edit.slot] === edit.value &&
		parameters[MAUI_EDIT_REVISION_PARAM] === edit.revisionId
	) {
		return undefined;
	}
	const continuesRevision = parameters[MAUI_EDIT_REVISION_PARAM] === edit.revisionId;
	tr.setNodeMarkup(pos, undefined, {
		...node.attrs,
		parameters: {
			...parameters,
			[MAUI_EDITABLE_TEXT_PARAM]: { ...overrides, [edit.slot]: edit.value },
			[MAUI_EDIT_REVISION_PARAM]: edit.revisionId,
		},
	});
	if (continuesRevision) {
		tr.setMeta('addToHistory', false);
	}
	return tr;
};

export const setRemixVersion = (
	tr: Transaction,
	node: PMNode,
	pos: number,
	versionId: string,
): Transaction => {
	tr.setNodeMarkup(pos, undefined, {
		...node.attrs,
		parameters: setParameters(node.attrs.parameters, { remixAppId: versionId }),
	});
	return tr.setMeta('addToHistory', false);
};

export const normalizeRemixVersions = (tr: Transaction): Transaction | undefined => {
	let changed = false;
	tr.doc.descendants((node, pos) => {
		if (!isNativeEmbedNode(node)) {
			return true;
		}
		const revisionId = getMauiEditRevisionId(node);
		const versionId = getRemixVersionForRevision(node.attrs.localId, revisionId);
		if (!versionId || getParameters(node.attrs.parameters, 'remixAppId') === versionId) {
			return false;
		}
		setRemixVersion(tr, node, pos, versionId);
		changed = true;
		return false;
	});
	return changed ? tr : undefined;
};
